/**
 * Firebase client SDK — graceful no-op when `firebase` is not installed.
 *
 * To enable push notifications:
 *   1. npm install firebase
 *   2. Add to .env:
 *        VITE_FIREBASE_API_KEY, VITE_FIREBASE_AUTH_DOMAIN,
 *        VITE_FIREBASE_PROJECT_ID, VITE_FIREBASE_STORAGE_BUCKET,
 *        VITE_FIREBASE_MESSAGING_SENDER_ID, VITE_FIREBASE_APP_ID,
 *        VITE_FIREBASE_VAPID_KEY
 *
 * Uses new Function('m','return import(m)') so Vite's static import-analysis
 * never sees the firebase package strings — no build error when not installed.
 */

// Indirect dynamic import — invisible to Vite's resolver
const _import = new Function("m", "return import(m)");

const _isConfigured = () => !!import.meta.env.VITE_FIREBASE_API_KEY;

const _cfg = () => ({
  apiKey:            import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain:        import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId:         import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket:     import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId:             import.meta.env.VITE_FIREBASE_APP_ID,
});

let _app       = null;
let _messaging = null;

async function _getApp() {
  if (_app) return _app;
  if (!_isConfigured()) return null;
  try {
    const { initializeApp, getApps } = await _import("firebase/app");
    _app = getApps().length ? getApps()[0] : initializeApp(_cfg());
    return _app;
  } catch {
    return null;
  }
}

async function _getMessaging() {
  if (_messaging) return _messaging;
  const app = await _getApp();
  if (!app) return null;
  try {
    const { getMessaging } = await _import("firebase/messaging");
    _messaging = getMessaging(app);
    return _messaging;
  } catch {
    return null;
  }
}

/**
 * Request notification permission and return an FCM token, or null.
 */
export async function requestFcmToken() {
  const vapidKey = import.meta.env.VITE_FIREBASE_VAPID_KEY;
  if (!vapidKey || !_isConfigured()) return null;
  try {
    if (!("Notification" in window)) return null;
    const permission = await Notification.requestPermission();
    if (permission !== "granted") return null;

    const messaging = await _getMessaging();
    if (!messaging) return null;

    const { getToken } = await _import("firebase/messaging");

    let swReg;
    try { swReg = await navigator.serviceWorker.register("/firebase-messaging-sw.js"); }
    catch { swReg = undefined; }

    const token = await getToken(messaging, {
      vapidKey,
      ...(swReg ? { serviceWorkerRegistration: swReg } : {}),
    });
    return token || null;
  } catch (e) {
    console.warn("[Firebase] requestFcmToken:", e.message);
    return null;
  }
}

/**
 * Listen for foreground push messages. Returns unsubscribe function.
 */
export async function onForegroundMessage(handler) {
  try {
    const messaging = await _getMessaging();
    if (!messaging) return () => {};
    const { onMessage } = await _import("firebase/messaging");
    return onMessage(messaging, handler);
  } catch {
    return () => {};
  }
}

/**
 * Returns the Firebase config object (for injecting into the service worker).
 */
export function getFirebaseConfig() {
  return _isConfigured() ? _cfg() : null;
}
